import React from "react";

// MUI imports - REQUIRED for DataGrid support
// ThemeProvider/createTheme provide theming for remaining MUI components (DataGrid, Autocomplete)
// StyledEngineProvider ensures correct CSS injection order
// These imports should be removed only after full DataGrid migration is complete
import {
  StyledEngineProvider,
  ThemeProvider,
  createTheme,
} from "@mui/material/styles";

import breakpoints from "./breakpoints";
import GlobalStyles from "./globalStyles";
import componentsOverride from "./overrides";
import palette from "./palette";
import shadows, { customShadows } from "./shadows";
import shape from "./shape";
import typography from "./typography";
import useSettings from "../hooks/useSettings";

// ----------------------------------------------------------------------

interface ThemeConfigProps {
  children: React.ReactNode;
}

export default function ThemeConfig({
  children,
}: ThemeConfigProps): React.ReactElement {
  const { themeMode, themeDirection } = useSettings();
  const isLight = themeMode === "light";

  const themeOptions = React.useMemo(
    () => ({
      palette: isLight ? { ...palette.light } : { ...palette.dark },
      spacing: 5,
      shape,
      typography,
      breakpoints,
      direction: themeDirection,
      shadows: isLight ? shadows.light : shadows.dark,
      customShadows: isLight ? customShadows.light : customShadows.dark,
    }),
    [isLight, themeDirection]
  );

  // Memoize theme creation to provide stable reference for child components
  // Without this, every ThemeConfig re-render creates a new theme object,
  // causing ALL components using useTheme() to re-render unnecessarily
  // Sync Tailwind dark mode class with MUI theme mode
  React.useEffect(() => {
    document.documentElement.classList.toggle("dark", themeMode === "dark");
  }, [themeMode]);

  const theme = React.useMemo(() => {
    // @ts-expect-error
    const baseTheme = createTheme(themeOptions);
    baseTheme.components = componentsOverride(baseTheme);
    return baseTheme;
  }, [themeOptions]);

  return (
    <StyledEngineProvider>
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
